// User-friendly formatting for audit log entries - param names and values.
// Used by C18SidebarAuditLog00 (and later the Admin > Audit Logs page).
// Every transform falls back to the raw value when it cannot map - it must
// never hide or corrupt what actually changed.
import {globs} from '../../components/utils/utils';
import {pInd,pi} from '../../components/utils/paramIds';
import {getDatapointName} from '../../usa/utils/utils';
import {chTypesExp} from '../../fui/utilsFui';

const NOT_SET="(not set)"

// Channel Type pid (508). Stored value is the type FAMILY
// (Math.floor(chTypeId/10) in saveChannelTypeToDb): 3=CO2, 10=Mix Valve.
const CHTYPE_I=pInd[1800].channels_configuration[0]+
  pi[1800].channels_configuration["channelType"]
const CHANNEL_TYPE_NAMES={0:"None",1:"On Off",2:"Irrigation",3:"CO2",
  4:"Light",5:"Microzone",6:"Pump",7:"Fill Valve",8:"Vent",9:"Curtain",
  10:"Mix Valve",11:"Proportional Microzone",12:"PID",18:"Variable Out",
  28:"Mix Valve Analog",29:"DLI Analog"}

const CHAN_DATA_BASE=pInd[1800].config_channels_configuration[0]

// Time-of-day params (minutes since midnight -> clock time). Durations
// (on-time, pulse widths) are NOT here - minutes there mean length.
const TIME_OF_DAY_PIDS={}
;["end_time","start_time","start_time_1","start_time_2","start_time_3",
  "start_time_4","start_time_5","start_time_6","daytime_start",
  "nighttime_start"].forEach(k=>{
  let ofs=pi[1800].config_channels_configuration[k]
  if(ofs!=null){TIME_OF_DAY_PIDS[CHAN_DATA_BASE+ofs]=1}
})

// Mode pids -> chType family. The stored mode is chTypeId%10, so the label is
// the chTypesExp entry at family*10+mode (e.g. Irrigation Mode 2 -> id 22 ->
// "Irrigation Cycle").
const MODE_PID_FAMILY={}
;[["irrigation_mode",2],["light_mode",4],["pump_type",6],["vent_type",8]]
  .forEach(([k,fam])=>{
  let ofs=pi[1800].config_channels_configuration[k]
  if(ofs!=null){MODE_PID_FAMILY[CHAN_DATA_BASE+ofs]=fam}
})

// 0-1439 minutes -> "h:mm AM/PM"; anything else returns null (raw stays shown)
var minutesToTimeStr=(v)=>{
  v=+v
  if(isNaN(v)||(v<0)||(v>1439)||(v!=Math.floor(v))){return null}
  let h24=Math.floor(v/60), m=v%60
  let h=h24%12
  if(h==0){h=12}
  return `${h}:${String(m).padStart(2,"0")} ${(h24<12)?"AM":"PM"}`
}

// 0 -> offLbl, 1 or 255 (FUI checkbox "on") -> onLbl, anything else -> null
var boolLabel=(v,onLbl,offLbl)=>{
  if((+v)===0){return offLbl}
  if(((+v)===1)||((+v)===255)){return onLbl}
  return null
}

var modeLabel=(pid,v)=>{
  let fam=MODE_PID_FAMILY[+pid]
  if((fam==null)||isNaN(+v)){return null}
  let id=fam*10+(+v)
  let hit=Object.values(chTypesExp[1800]||{}).find(e=>e.id==id)
  return hit?.t??null
}

// pid -> {value -> label} harvested once from the select controls in the FUI
// page definitions (customProps.options). Covers sensor selectors, output
// types etc. with the exact labels the user picked from. First label wins on
// the rare pid that appears on several pages.
var optionsMap=null
var getOptionLabel=(pid,v)=>{
  if(!optionsMap){
    let idx=globs.fuiPagesInfo?.index
    if(!idx){return null}// pages not loaded yet - try again next entry
    optionsMap={}
    Object.values(idx).forEach(pg=>{
      (pg?.controls||[]).forEach(ct=>{
        let opts=ct?.customProps?.options
        if(Array.isArray(opts)&&((+ct.pid)>=0)){
          let m=optionsMap[+ct.pid]||(optionsMap[+ct.pid]={})
          opts.forEach(o=>{if(m[o.v]===undefined){m[o.v]=o.t}})
        }
      })
    })
  }
  return optionsMap[+pid]?.[+v]??null
}

/* The one entry point: raw db00h row -> {parmName, oldVal, newVal} for
display. Rules, most specific first; every miss falls back to the raw value. */
var formatAuditEntry=(c)=>{
  let parmName=getDatapointName(c?.i,globs.siteZoneTypes?.[c?.z])
// "Config Channels Configuration - CD Cool 3 Onoff" -> "... - Cool 3 Onoff":
// the CD_ prefix means nothing to users. It sits after the table label, so
// strip it at the start OR right after the "- " separator.
  if(typeof parmName=="string"){
    parmName=parmName.replace(/(^|- )CD /,"$1")
  }
  let oldVal=(c?.o||(c?.o===0))?c.o:NOT_SET
  let newVal=c?.d
  let isStd=(globs.siteZoneTypes?.[c?.z]!=800)// 800-series stays numeric
  let mapVal=(v)=>{
    if(v===NOT_SET){return v}
// Channel Type: family name ("None -> CO2" instead of "0 -> 3")
    if(((+c?.i)==CHTYPE_I)&&isStd){return CHANNEL_TYPE_NAMES[+v]??v}
// Start/End Time: minutes -> clock time ("0 -> 720" -> "12:00 AM -> 12:00 PM")
    if(TIME_OF_DAY_PIDS[+c?.i]&&isStd){return minutesToTimeStr(v)??v}
// Mode dropdowns: "Irrigation Mode 1 -> 2" -> "Irrigation Scheduled -> Irrigation Cycle"
    if(MODE_PID_FAMILY[+c?.i]&&isStd){return modeLabel(c?.i,v)??v}
// Any select control: value -> its option label ("Water Temperature Sensor
// 0 -> 5" -> "Analog Temperature 1 -> None")
    if(isStd){
      let lbl=getOptionLabel(c?.i,v)
      if(lbl!=null){return lbl}
    }
// on/off params: "0 -> 1" -> "Off -> On"
    if((typeof parmName=="string")&&/onoff\s*$/i.test(parmName)){
      return boolLabel(v,"On","Off")??v
    }
// enable flags: "0 -> 1" -> "Disabled -> Enabled"
    if((typeof parmName=="string")&&/enabled?\s*$/i.test(parmName)){
      return boolLabel(v,"Enabled","Disabled")??v
    }
    return v
  }
  return {parmName:parmName, oldVal:mapVal(oldVal), newVal:mapVal(newVal)}
}

export {formatAuditEntry, NOT_SET}
